import PoliciesForm from './PoliciesForm';
import ConfirmationDialog from '../../../components/messages/ConfirmationDialog';
import LoadingButton from '../../../components/inputs/LoadingButton';
import { useMutation } from '@tanstack/react-query'; 
import { AxiosError } from 'axios';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { useRef } from 'react';
import { Toast } from 'primereact/toast';
import { useFormContext } from 'react-hook-form';
import { Policy, PolicyForm } from '../../../Interfaces/Insurance/Policy';
import { usePolicySummaryContext } from './PolicyContext';
import { putPolicy } from '../../../services/Insurance/Policy';

type UpdatePolicyProps = {
  id: string;
  prevAttachment?: string;
  policyType: string;
  afterUpdate: (policy: Policy) => void;
};

const UpdatePolicy = ({
  id,
  prevAttachment,
  policyType,
  afterUpdate,
}: UpdatePolicyProps) => {
  const formMethods = useFormContext<PolicyForm>();
  const { updateOne } = usePolicySummaryContext();
  const { handleSubmit } = formMethods;
  const toast = useRef<Toast>(null);

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (data: PolicyForm) => {
      return putPolicy(id, data);
    },
    onSuccess: (policy) => {
      const { attachmentPath, notes, ...otherFields } = policy;
      updateOne(otherFields);
      afterUpdate(policy);
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Policy Update Error',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const onSubmit = (data: PolicyForm) => {
    mutate(data);
  };

  if (isSuccess) {
    return (
      <div className="w-full mx-auto flex flex-column gap-5">
        <div className="w-fit mx-auto flex flex-column">
          <div className="text-center text-40px font-bold text-green-500 mb-3">
            Success!
          </div>
          <i className="pi pi-check-circle mx-auto text-40px text-green-500 mb-3" />
          <div className="text-standard mt-3 mx-auto">
            The Policy has been updated successfully!
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className="w-full mx-auto flex flex-column gap-5">
      <PoliciesForm
        prevAttachment={prevAttachment}
        policyTypeDefaultValue={policyType}
      />
      <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
        <ConfirmationDialog
          Button={
            <LoadingButton
              isLoading={isPending}
              label="Update"
              className="w-8rem bluwaiBlue border-0 flex m-auto py-3"
              fontSize="text-20px"
              type="button"
            />
          }
          onConfirm={handleSubmit(onSubmit)}
          message="Are you sure you want to update the policy?"
        />
      </div>
      <ErrorToast toastRef={toast} />
    </form>
  );
};

export default UpdatePolicy;
